import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ingredientAggregator, ingredientParser, type AggregatedShoppingItem, type MealPlanShoppingListData } from '@/services/ingredients'
import { Calendar, Check, ChevronDown, ChevronRight, Download, Edit3, Package, Plus, ShoppingCart, Trash2 } from 'lucide-react'

interface ShoppingListPreviewProps {
  shoppingData: MealPlanShoppingListData
  onItemUpdate?: (itemIndex: number, updates: Partial<AggregatedShoppingItem>) => void
  onItemAdd?: (item: Omit<AggregatedShoppingItem, 'recipe_ids' | 'recipe_titles' | 'category'>) => void
  onItemRemove?: (itemIndex: number) => void
  onGenerate?: () => void
  onExport?: (format: 'text' | 'csv' | 'json') => void
  isGenerating?: boolean
  className?: string
}

export function ShoppingListPreview({
  shoppingData,
  onItemUpdate,
  onItemAdd,
  onItemRemove,
  onGenerate,
  onExport,
  isGenerating = false,
  className,
}: ShoppingListPreviewProps) {
  const [editingItem, setEditingItem] = useState<number | null>(null)
  const [newItemText, setNewItemText] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())

  const totalItems = shoppingData.all_items.length
  const checkedItems = shoppingData.all_items.filter((item) => item.is_purchased).length

  const dateRangeLabel = (() => {
    const start = new Date(shoppingData.date_range.start_date)
    const end = new Date(shoppingData.date_range.end_date)

    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })
    }

    return `${start.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
    })}`
  })()

  const handleItemEdit = (
    itemIndex: number,
    field: keyof AggregatedShoppingItem,
    value: AggregatedShoppingItem[typeof field]
  ) => {
    onItemUpdate?.(itemIndex, { [field]: value })
  }

  const handleAddCustomItem = () => {
    if (!newItemText.trim() || !onItemAdd) {
      return
    }

    const parsed = ingredientParser.parseIngredient(newItemText)
    onItemAdd({
      name: parsed.item,
      quantity: parsed.quantity,
      unit: parsed.unit,
      specification: parsed.specification,
      is_purchased: false,
      notes: 'Handmatig toegevoegd',
    })

    setNewItemText('')
    setShowAddForm(false)
  }

  const toggleCategoryCollapse = (category: string) => {
    setCollapsedCategories((current) => {
      const next = new Set(current)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  return (
    <Card className={className}>
      <CardHeader className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Voorvertoning
            </CardTitle>
            <p className="text-sm text-gray-600">Controleer de lijst, pas items aan en maak daarna de definitieve versie.</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {dateRangeLabel}
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1">
              <Package className="h-3.5 w-3.5" />
              {totalItems} items
            </Badge>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Maaltijden</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">{shoppingData.total_meals}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Recepten</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">{shoppingData.total_recipes}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Categorieën</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">{shoppingData.categories.length}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">Afgevinkt</div>
            <div className="mt-1 text-xl font-semibold text-gray-900">
              {checkedItems}/{totalItems}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {shoppingData.categories.map((categoryGroup) => {
          const isCollapsed = collapsedCategories.has(categoryGroup.category)

          return (
            <div key={categoryGroup.category} className="overflow-hidden rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => toggleCategoryCollapse(categoryGroup.category)}
                className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left hover:bg-gray-100"
              >
                <div className="flex items-center gap-3">
                  {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                  <div>
                    <div className="font-medium text-gray-900">
                      {ingredientAggregator.getCategoryDisplayName(categoryGroup.category)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {categoryGroup.total_items} items
                    </div>
                  </div>
                </div>

                <Badge variant="outline">
                  {categoryGroup.purchased_items}/{categoryGroup.total_items}
                </Badge>
              </button>

              {!isCollapsed && (
                <div className="space-y-2 p-3">
                  {categoryGroup.items.map((item) => {
                    const globalIndex = shoppingData.all_items.findIndex((candidate) => candidate === item)
                    const isEditing = editingItem === globalIndex

                    return (
                      <div
                        key={`${categoryGroup.category}-${globalIndex}`}
                        className={`flex items-start gap-3 rounded-lg border p-3 ${
                          item.is_purchased ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <button
                          onClick={() => handleItemEdit(globalIndex, 'is_purchased', !item.is_purchased)}
                          className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border ${
                            item.is_purchased ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300'
                          }`}
                          aria-label={item.is_purchased ? 'Markeer als niet gekocht' : 'Markeer als gekocht'}
                        >
                          {item.is_purchased && <Check className="h-3.5 w-3.5" />}
                        </button>

                        <div className="min-w-0 flex-1">
                          {isEditing ? (
                            <div className="space-y-2">
                              <Input
                                value={item.name}
                                onChange={(event) => handleItemEdit(globalIndex, 'name', event.target.value)}
                              />
                              <div className="grid gap-2 sm:grid-cols-[100px_110px_minmax(0,1fr)]">
                                <Input
                                  type="number"
                                  value={item.quantity || ''}
                                  onChange={(event) =>
                                    handleItemEdit(
                                      globalIndex,
                                      'quantity',
                                      event.target.value ? Number(event.target.value) : undefined
                                    )
                                  }
                                  placeholder="Hoeveelheid"
                                />
                                <Input
                                  value={item.unit || ''}
                                  onChange={(event) => handleItemEdit(globalIndex, 'unit', event.target.value)}
                                  placeholder="Eenheid"
                                />
                                <Input
                                  value={item.specification || ''}
                                  onChange={(event) => handleItemEdit(globalIndex, 'specification', event.target.value)}
                                  placeholder="Specificatie"
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className={`font-medium ${item.is_purchased ? 'line-through text-green-800' : 'text-gray-900'}`}>
                                {ingredientParser.formatShoppingListItem(item)}
                              </div>
                              {item.recipe_titles.length > 0 && (
                                <div className="mt-1 text-sm text-gray-600">{item.recipe_titles.join(', ')}</div>
                              )}
                              {item.notes && <div className="mt-1 text-xs text-gray-500">{item.notes}</div>}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingItem(isEditing ? null : globalIndex)}
                            className="h-8 w-8 p-0"
                          >
                            {isEditing ? <Check className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}
                          </Button>

                          {onItemRemove && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onItemRemove(globalIndex)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}

        {onItemAdd && (
          <div className="rounded-lg border border-dashed border-gray-200 p-4">
            {showAddForm ? (
              <div className="space-y-3">
                <Input
                  value={newItemText}
                  onChange={(event) => setNewItemText(event.target.value)}
                  placeholder="Voeg item toe, bijvoorbeeld 2 kg appels"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleAddCustomItem()
                    }
                  }}
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button onClick={handleAddCustomItem} size="sm">
                    <Plus className="mr-2 h-4 w-4" />
                    Toevoegen
                  </Button>
                  <Button onClick={() => setShowAddForm(false)} size="sm" variant="outline">
                    Annuleren
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => setShowAddForm(true)} variant="ghost" className="w-full text-gray-600 hover:text-gray-900">
                <Plus className="mr-2 h-4 w-4" />
                Item handmatig toevoegen
              </Button>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          {onGenerate && (
            <Button onClick={onGenerate} disabled={isGenerating}>
              <ShoppingCart className="mr-2 h-4 w-4" />
              {isGenerating ? 'Definitieve lijst maken...' : 'Definitieve lijst maken'}
            </Button>
          )}

          {onExport && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => onExport('text')} variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Tekst kopiëren
              </Button>
              <Button onClick={() => onExport('csv')} variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                CSV
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
